import React, { useState } from 'react'

import { motion, AnimatePresence } from 'framer-motion'

import { Swiper, SwiperSlide } from 'swiper/react'
import { FreeMode, Mousewheel } from 'swiper'

import { Paragraph } from '@/atoms/Typography'
import { Container } from '@/atoms/Grid'

import Card from './Card'
import Image from './Image'
import Text from './Documents'
import Table from './Table'
import Video from './Video'

import {
  StyledTab,
  StyledTabInner,
  StyledTabHeader,
  StyledTabHeaderItemList,
  StyledTabHeaderItem,
  StyledTabContent,
} from './styles'

const swiperConfig = {
  slidesPerView: 'auto',
  freeMode: {
    enabled: true,
    sticky: false,
    momentumBounce: false,
    momentumVelocityRatio: 0.5,
  },
  mousewheel: {
    enabled: true,
    forceToAxis: true,
  },
}

const Tabs = ({ tabs, minimal }) => {
  const [selectedTab, setSelectedTab] = useState(0)

  const handleTabChange = (index) => {
    setSelectedTab(index)
  }

  const handleTabListKeyDown = (ev) => {
    const target = ev.target;
    const tabEl = target && target.closest ? target.closest('[role="tab"]') : null;
  
    let currentIndex = -1;
    if (tabEl && tabEl.id && tabEl.id.startsWith('tab-')) {
      const activeId = tabEl.id.replace('tab-', '');
      currentIndex = tabs.findIndex((t) => String(t.id) === String(activeId));
    }
  
    if (currentIndex < 0) currentIndex = selectedTab;
  
    if (currentIndex >= 0) {
      handleRovingKeyDown(currentIndex, ev);
    }
  };

  const focusTabByIndex = (index) => {
    const tabId = `tab-${tabs[index].id}`
    const el = typeof document !== 'undefined' ? document.getElementById(tabId) : null
    if (el) el.focus({ preventScroll: true })
  }

  const handleRovingKeyDown = (currentIndex, ev) => {
    const key = ev.key
    const lastIndex = tabs.length - 1
    let nextIndex = null

    if (key === 'ArrowRight') {
      ev.preventDefault()
      nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1
    } else if (key === 'ArrowLeft') {
      ev.preventDefault()
      nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1
    } else if (key === 'Home') {
      ev.preventDefault()
      nextIndex = 0
    } else if (key === 'End') {
      ev.preventDefault()
      nextIndex = lastIndex
    } else if (key === ' ' || key === 'Enter') {
      ev.preventDefault()
      handleTabChange(currentIndex)
      return
    }

    if (nextIndex !== null) {
      handleTabChange(nextIndex)
      focusTabByIndex(nextIndex)
    }
  }

  const renderTabContent = (type = 'card') => {
    const normalizeContent = {
      card: Card,
      image: Image,
      documents: Text,
      table: Table,
      video: Video,
    }

    const Component = normalizeContent[type]

    return <Component {...tabs[selectedTab]} />
  }

  return (
    <StyledTab
      minimal={minimal || tabs[selectedTab].type == 'documents' ? true : null}
    >
      <Container>
        <StyledTabInner>
          <StyledTabHeader>
            <StyledTabHeaderItemList role='tablist' aria-orientation='horizontal' onKeyDown={handleTabListKeyDown}>
              <Swiper
                modules={[FreeMode, Mousewheel]}
                {...swiperConfig}
              >
                {tabs.map(({ id, label }, index) => {
                  return (
                    <SwiperSlide key={id}>
                      <StyledTabHeaderItem
                        role='tab'
                        id={`tab-${id}`}
                        aria-selected={selectedTab === index}
                        aria-controls={`panel-${id}`}
                        tabIndex={selectedTab === index ? 0 : -1}
                        isSelected={selectedTab === index}
                        onClick={() => handleTabChange(index)}
                        onKeyDown={(ev) => handleRovingKeyDown(index, ev)}
                      >
                        <Paragraph
                          typo={'paragraph2'}
                          bold
                        >
                          {label}
                        </Paragraph>
                      </StyledTabHeaderItem>
                    </SwiperSlide>
                  )
                })}
              </Swiper>
            </StyledTabHeaderItemList>
          </StyledTabHeader>
          <StyledTabContent>
            <AnimatePresence mode={'wait'}>
              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -10, opacity: 0 }}
                transition={{ duration: 0.2 }}
                key={selectedTab}
                role='tabpanel'
                id={`panel-${tabs[selectedTab].id}`}
                aria-labelledby={`tab-${tabs[selectedTab].id}`}
              >
                {renderTabContent(tabs[selectedTab].type)}
              </motion.div>
            </AnimatePresence>
          </StyledTabContent>
        </StyledTabInner>
      </Container>
    </StyledTab>
  )
}

export default Tabs
